/**
 * Login form component for DEX UI.
 * Expandable inline form with API key input and stored keys list.
 */

import { getState, subscribe } from '../../state/store.js';
import { authenticateKey, validateKeyFormat, maskApiKey } from '../../api/auth.js';
import {
  setAuthenticated,
  setAuthError,
  setLoading,
  toggleLoginExpanded,
  clearSession
} from '../../state/session.js';
import { storage } from '../../utils/storage.js';
import { renderRoleBadge } from './role-badge.js';

/**
 * Handle login form submission.
 * @param {Event} event - Form submit event
 */
async function handleLogin(event) {
  event.preventDefault();

  const form = event.target;
  const keyInput = form.querySelector('#api-key-input');
  const rememberCheckbox = form.querySelector('#remember-me');

  const apiKey = keyInput.value.trim();
  const rememberMe = rememberCheckbox?.checked || false;

  // Quick validation
  const formatCheck = validateKeyFormat(apiKey);
  if (!formatCheck.valid) {
    setAuthError(formatCheck.hint);
    return;
  }

  // Start loading
  setLoading(true);

  try {
    // Authenticate via API
    const authData = await authenticateKey(apiKey);

    // Success - update session
    setAuthenticated({
      ...authData,
      rememberMe
    });
  } catch (error) {
    setAuthError(error.message || 'Authentication failed');
  }
}

/**
 * Handle switching to a stored key.
 * @param {string} key - API key to switch to
 */
async function handleSwitchKey(key) {
  // Get stored key data
  const storedKeys = storage.getKeys();
  const keyData = storedKeys.find(k => k.key === key);

  if (!keyData) {
    setAuthError('Key not found');
    return;
  }

  setLoading(true);

  try {
    const authData = await authenticateKey(key);
    setAuthenticated({
      ...authData,
      rememberMe: storage.getRememberMe()
    });
  } catch (error) {
    setAuthError(error.message || 'Authentication failed');
  }
}

/**
 * Handle logout button click.
 */
function handleLogout() {
  // Per requirements: Logout confirmation only if unsaved work exists
  // For now, no unsaved work concept in auth phase - direct logout
  clearSession();
}

/**
 * Render the login form into a container.
 * @param {HTMLElement} container - Container element
 */
export function renderLoginForm(container) {
  const { session, storedKeys, ui } = getState();

  // If authenticated, show role badge and logout button
  if (session.authenticated) {
    container.innerHTML = `
      <div class="flex items-center gap-3">
        <div id="role-badge-container"></div>
        <button
          id="logout-btn"
          class="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
        >
          Logout
        </button>
      </div>
    `;

    // Initialize role badge
    const badgeContainer = container.querySelector('#role-badge-container');
    renderRoleBadge(badgeContainer);

    // Attach logout handler
    container.querySelector('#logout-btn').addEventListener('click', handleLogout);
    return;
  }

  // Not authenticated - show login button or expanded form
  if (!ui.loginExpanded) {
    container.innerHTML = `
      <button
        id="login-toggle"
        class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
      >
        Login
      </button>
    `;

    container.querySelector('#login-toggle').addEventListener('click', () => {
      toggleLoginExpanded();
    });
    return;
  }

  // Show expanded login form
  const hasStoredKeys = storedKeys.length > 0 && storage.getRememberMe();

  container.innerHTML = `
    <div class="relative">
      <button
        id="login-close"
        class="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600"
        aria-label="Close"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>

      <div class="bg-white border rounded-lg shadow-lg p-4 w-80">
        <form id="login-form" class="space-y-3">
          <div>
            <label for="api-key-input" class="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <div class="relative">
              <input
                type="${ui.showKey ? 'text' : 'password'}"
                id="api-key-input"
                class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${ui.error ? 'border-red-500' : 'border-gray-300'}"
                placeholder="dx_role_..."
                autocomplete="off"
                spellcheck="false"
              />
              <button
                type="button"
                id="toggle-visibility"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label="${ui.showKey ? 'Hide' : 'Show'} API key"
              >
                ${ui.showKey
                  ? '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path></svg>'
                  : '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>'
                }
              </button>
            </div>
            ${ui.error ? `<p class="text-red-500 text-xs mt-1">${ui.error}</p>` : ''}
          </div>

          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              id="remember-me"
              class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span class="text-sm text-gray-600">Remember me</span>
          </label>

          <button
            type="submit"
            class="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            ${ui.loading ? 'disabled' : ''}
          >
            ${ui.loading ? 'Validating...' : 'Login'}
          </button>
        </form>

        ${hasStoredKeys ? `
          <div class="mt-4 pt-4 border-t border-gray-200">
            <h4 class="text-sm font-medium text-gray-700 mb-2">Saved Keys</h4>
            <ul class="space-y-1 max-h-32 overflow-y-auto">
              ${storedKeys.map(k => `
                <li>
                  <button
                    class="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 flex items-center gap-2 transition-colors"
                    data-switch-key="${k.key}"
                  >
                    <span class="w-2 h-2 rounded-full role-dot-${k.role}"></span>
                    <span class="text-sm text-gray-700 truncate flex-1">${maskApiKey(k.key)}</span>
                    <span class="text-xs text-gray-500">${k.role}</span>
                  </button>
                </li>
              `).join('')}
            </ul>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  // Attach event handlers
  container.querySelector('#login-close')?.addEventListener('click', () => {
    toggleLoginExpanded();
  });

  container.querySelector('#toggle-visibility')?.addEventListener('click', (e) => {
    const input = container.querySelector('#api-key-input');
    const button = e.currentTarget;
    if (input) {
      const isPassword = input.type === 'password';
      input.type = isPassword ? 'text' : 'password';
      // Update button aria-label and icon
      button.setAttribute('aria-label', isPassword ? 'Hide API key' : 'Show API key');
      // Swap icon SVG
      if (isPassword) {
        // Show "hide" icon (eye with slash)
        button.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path></svg>';
      } else {
        // Show "show" icon (eye open)
        button.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>';
      }
    }
  });

  container.querySelector('#login-form')?.addEventListener('submit', handleLogin);

  // Real-time validation as user types (per user decision: "Inline real-time validation as user types/pastes")
  const keyInput = container.querySelector('#api-key-input');
  if (keyInput) {
    keyInput.addEventListener('input', (e) => {
      const value = e.target.value.trim();
      // Clear error if empty (user is clearing the field)
      if (!value) {
        if (getState().ui.error) {
          setAuthError(null);
        }
        return;
      }
      // Validate format and show inline error if invalid
      const formatCheck = validateKeyFormat(value);
      if (!formatCheck.valid) {
        setAuthError(formatCheck.hint);
      } else if (getState().ui.error) {
        // Clear error if now valid
        setAuthError(null);
      }
    });
  }

  // Stored key switch handlers
  container.querySelectorAll('[data-switch-key]').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.getAttribute('data-switch-key');
      handleSwitchKey(key);
    });
  });

  // Focus the input
  container.querySelector('#api-key-input')?.focus();
}

/**
 * Initialize login form with state subscription.
 * @param {HTMLElement} container - Container element
 * @returns {Function} Cleanup function
 */
export function initLoginForm(container) {
  // Initial render
  renderLoginForm(container);

  // Subscribe to state changes
  const unsubscribe = subscribe((state, path) => {
    if (path.includes('session') || path.includes('ui') || path.includes('storedKeys') || path === '*') {
      renderLoginForm(container);
    }
  });

  return unsubscribe;
}
